import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, ChatTeardropText, ArrowUpRight, CircleNotch } from "@phosphor-icons/react";

/**
 * Live "Conversas aguardando humano" widget for the operator Dashboard.
 * Auto-refreshes every 30s while the tab is visible.
 */

function timeAgo(iso) {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "agora";
  const m = Math.round(d / 60);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

function urgencyOf(iso) {
  if (!iso) return null;
  const min = (Date.now() - new Date(iso).getTime()) / 60000;
  if (min >= 60) return "high";
  if (min >= 15) return "medium";
  return null;
}

export default function AwaitingHumanWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/awaiting_human?limit=8");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !data) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <CircleNotch size={16} className="animate-spin text-slate-400" />
      </div>
    );
  }

  const total = data?.total || 0;
  const items = data?.items || [];

  // Hide widget entirely when no pending handoffs (don't add noise)
  if (total === 0) return null;

  const hasUrgent = items.some((i) => urgencyOf(i.waiting_since) === "high");

  return (
    <section
      data-testid="awaiting-human-widget"
      className={`relative overflow-hidden rounded-lg border bg-white transition-colors ${
        hasUrgent ? "border-amber-300" : "border-slate-200"
      }`}
    >
      {hasUrgent && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-300 animate-pulse" />
      )}

      <header className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
            hasUrgent ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"
          }`}>
            <Bell size={14} weight={hasUrgent ? "fill" : "regular"} />
          </div>
          <div>
            <h3 className="font-heading font-semibold tracking-tight text-[15px]">
              Aguardando você
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {total === 1 ? "1 conversa precisa" : `${total} conversas precisam`} de atendimento humano
            </p>
          </div>
        </div>
        <Link
          to="/app/conversas?filter=human_required"
          className="text-[12px] text-slate-500 hover:text-slate-900 font-medium transition-colors flex items-center gap-1"
          data-testid="awaiting-human-see-all"
        >
          Ver todas <ArrowUpRight size={11} weight="regular" />
        </Link>
      </header>

      <ul className="divide-y divide-slate-100">
        {items.map((it) => {
          const urg = urgencyOf(it.waiting_since);
          return (
            <li key={it.conversation_id}>
              <Link
                to={`/app/conversas/${it.conversation_id}`}
                data-testid={`awaiting-human-row-${it.conversation_id}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors group"
              >
                <div className={`w-1 self-stretch rounded-full ${
                  urg === "high" ? "bg-rose-400" : urg === "medium" ? "bg-amber-400" : "bg-slate-200"
                }`} />
                <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-[12px] font-semibold shrink-0">
                  {(it.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-slate-900 truncate">{it.name}</span>
                    {it.lead_id && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">lead</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate flex items-center gap-1.5">
                    <ChatTeardropText size={10} weight="regular" className="shrink-0" />
                    <span className="truncate">{it.last_message || "—"}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-[11px] tabular-nums ${
                    urg === "high" ? "text-rose-500 font-semibold" :
                    urg === "medium" ? "text-amber-600" : "text-slate-500"
                  }`}>
                    {timeAgo(it.waiting_since)}
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      {total > items.length && (
        <Link
          to="/app/conversas?filter=human_required"
          className="block text-center px-5 py-3 text-[12px] text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors border-t border-slate-100 font-medium"
        >
          + {total - items.length} {(total - items.length) === 1 ? "outra" : "outras"} aguardando
        </Link>
      )}
    </section>
  );
}
